import { get as _get } from "lodash";
import { useState } from "react"; // , {useState}
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// components mui
import { ClickAwayListener } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LogoutIcon from "@mui/icons-material/Logout";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Popper from "@mui/material/Popper";
import Identicon from "react-identicons";
import { useWallet, useWalletSession } from "../../hooks/useWallet";
import LogoWhite from "../../assets/images/logo_white.svg";
import { ConnectButton } from "../Buttons/ConnectButton";
// helpers
import shortedAddress from "../../utils/display";
// actions
import { CONFIG_BASE } from "../../constants/configs";
import { navRoutes, isRouteActive } from "../../constants/routes";
import { setModal, setModalData } from "../../redux/actions/modals";
import { DropdownNav } from "../DropdownNav";
import { KOINDX_TOKEN, useKoindxBalance } from "../../hooks/queries";
import { useExplorer } from "../../hooks/useExplorer";
import { explorerUrl } from "../../utils/explorer";
import TokenLogo from "../TokenLogo";
import { formatTokenAmount } from "../../utils/format";
import { amountType } from "../../theme/main";
import { disconnectWallet } from "./../../redux/actions/wallet";

// queries
import { setNetwork } from "./../../redux/actions/settings";

const Header = () => {
  // Dispatch to call actions
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // MediaQuery
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("sm"));

  // selectors
  const walletSelector = useWallet();
  const { clearSession } = useWalletSession();

  const removeTabInPaths = [];
  const [dropdownEl, setDropdownEl] = useState(null);
  const [justOpenedDropdown, setJustOpenedDropdown] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const address = _get(walletSelector, "wallet[0].address", "");
  const connected = _get(walletSelector, "connected", false);

  const explorer = useExplorer();

  // the KOINDX balance is only shown in the account drawer, it is loaded while the drawer is open
  const baseTokens = useSelector((state) => state.tokens.base_tokens);
  const koindxToken = baseTokens.find((token) => token.address === KOINDX_TOKEN) || {
    address: KOINDX_TOKEN,
    symbol: "KOINDX",
    decimals: 8,
  };
  const koindxQuery = useKoindxBalance(address, isDrawerOpen && connected);
  const koindxBalance =
    koindxQuery.data != null ? formatTokenAmount(koindxQuery.data, koindxToken.decimals, { maxDecimals: 4 }) : "…";

  const disconnect = async () => {
    const instance = _get(walletSelector, "instance", null);
    if (_get(walletSelector, "wallet_name", null) == "walletconnect" && instance) {
      instance.disconnect().catch((error) => console.log(error));
    }
    clearSession();
    dispatch(disconnectWallet());
    dispatch(setNetwork(CONFIG_BASE.chain.id));
    setIsDrawerOpen(false);
  };

  const openModalSettingsApp = () => {
    dispatch(setModalData(null));
    dispatch(setModal("SettingsApp"));
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(_get(walletSelector, "wallet[0].address", ""));
    } catch (err) {
      console.log(err);
    }
  };

  const handleDropDown = (event, _route) => {
    setDropdownEl(event.currentTarget);
    setJustOpenedDropdown(_route);
    event.stopPropagation();
  };
  const clickAway = () => {
    if (justOpenedDropdown != null) {
      setDropdownEl(null);
    }
    setJustOpenedDropdown(null);
  };
  // nav items: muted text, the active section gets white text and a mint underline on the header edge
  const navItemSx = (active) => ({
    position: "relative",
    height: 64,
    minWidth: 0,
    px: 1.5,
    borderRadius: 0,
    color: active ? "text.primary" : "text.secondary",
    fontWeight: 560,
    "&:hover": { color: "text.primary", backgroundColor: "transparent" },
    // the item is as tall as the bar: draw the keyboard ring around the label, not the whole bar
    "&.Mui-focusVisible": { outlineOffset: "-10px", borderRadius: "22px" },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 12,
      right: 12,
      bottom: -1,
      height: 2,
      borderRadius: "2px",
      backgroundColor: active ? "primary.main" : "transparent",
    },
  });

  const prepareRoutes = () =>
    navRoutes.map((route) => {
      const active = isRouteActive(route, location.pathname);
      if (route.type == "dropdown") {
        return (
          <Button
            key={route.index}
            sx={navItemSx(active || justOpenedDropdown == route.route)}
            aria-haspopup="true"
            aria-expanded={justOpenedDropdown == route.route}
            endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5 }} />}
            onClick={(event) => handleDropDown(event, route.route)}
          >
            {route.label}
          </Button>
        );
      }
      return (
        <Button
          key={route.index}
          sx={navItemSx(active)}
          aria-current={active ? "page" : undefined}
          onClick={() => navigate(route.route)}
        >
          {route.label}
        </Button>
      );
    });

  const prepareDropdowns = () =>
    navRoutes
      .filter((r) => r.type == "dropdown")
      .map((route) => (
        <ClickAwayListener onClickAway={clickAway} key={route.index} disableReactTree={true}>
          <Popper
            id={route.route == justOpenedDropdown ? "header-dropdown-menu" : null}
            open={route.route == justOpenedDropdown}
            anchorEl={dropdownEl}
            placement="bottom-start"
          >
            <DropdownNav dropdownRoutes={route.childrens} dropdownClose={clickAway} />
          </Popper>
        </ClickAwayListener>
      ));

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        backgroundImage: "none",
        marginBottom: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 }, gap: { xs: 1, sm: 3 } }}>
          <Box
            component="button"
            type="button"
            aria-label="KoinDX home"
            onClick={() => navigate("/")}
            sx={{ display: "flex", p: 0, border: 0, background: "none", cursor: "pointer", borderRadius: 1 }}
          >
            <img src={LogoWhite} height={24} alt="KoinDX" />
          </Box>
          {!matches ? (
            <Box component="nav" aria-label="Main" sx={{ display: "flex", alignItems: "stretch" }}>
              {removeTabInPaths.indexOf(location.pathname) === -1 ? prepareRoutes() : null}
              {removeTabInPaths.indexOf(location.pathname) === -1 ? prepareDropdowns() : null}
            </Box>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton aria-label="settings" onClick={() => openModalSettingsApp()}>
              <SettingsIcon fontSize="small" />
            </IconButton>
            {connected ? (
              <Button
                variant="outlined"
                aria-label="account"
                onClick={() => {
                  setIsDrawerOpen(true);
                }}
                sx={{ gap: 1, paddingInline: 1.5 }}
              >
                <Identicon string={address} size={16} palette={[theme.palette.primary.main]} />
                {shortedAddress(address)}
              </Button>
            ) : (
              <Box sx={{ width: { xs: "auto", sm: 148 } }}>
                <ConnectButton />
              </Box>
            )}
          </Box>
        </Toolbar>

        <Drawer
          anchor={"right"}
          open={isDrawerOpen && connected}
          onClose={() => setIsDrawerOpen(false)}
          slotProps={{
            paper: { sx: { width: { xs: "88%", sm: 380 } } },
          }}
        >
          <Box role="presentation" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h6" component="h2">
              Account
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Identicon string={address} size={28} palette={[theme.palette.primary.main]} />
              <Typography sx={{ flexGrow: 1, fontWeight: 560 }}>{shortedAddress(address)}</Typography>
              <Tooltip title="Copy address">
                <IconButton aria-label="copy address" onClick={() => copyAddress()}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={`View on ${explorer.name}`}>
                <IconButton
                  aria-label={`view account on ${explorer.name}`}
                  href={explorerUrl(explorer, "address", address)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {KOINDX_TOKEN ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "background.light",
                }}
              >
                <TokenLogo token={koindxToken} size={32} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{koindxToken.symbol}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Balance
                  </Typography>
                </Box>
                <Typography sx={{ ...amountType, fontSize: "1.375rem" }} noWrap>
                  {koindxBalance}
                </Typography>
              </Box>
            ) : null}
            <Button variant="outlined" startIcon={<LogoutIcon fontSize="small" />} onClick={() => disconnect()}>
              Disconnect
            </Button>
          </Box>
        </Drawer>
      </Container>
    </AppBar>
  );
};

export default Header;
